import { useState, useEffect } from "react";
import { format } from "date-fns";
import { CalendarIcon, MapPin, Users, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { buildWhatsAppLink } from "@/lib/config";

const TRIP_LABELS = {
  local: "Local ride",
  outstation: "Outstation trip",
  airport: "Airport transfer",
};

export const BookingForm = ({ prefill }) => {
  const [tripType, setTripType] = useState("local");
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    pickup_location: "",
    drop_location: "",
    pickup_time: "09:00",
    vehicle_type: "sedan",
    passengers: 2,
    notes: "",
  });

  useEffect(() => {
    if (!prefill) return;
    if (prefill.tripType) setTripType(prefill.tripType);
    setForm((s) => ({
      ...s,
      pickup_location: prefill.pickup ?? s.pickup_location,
      drop_location: prefill.drop ?? s.drop_location,
    }));
  }, [prefill]);

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.pickup_location || !date) {
      toast.error("Please fill Name, Phone, Pickup and Date");
      return;
    }
    setSubmitting(true);
    const payload = {
      ...form,
      email: form.email || undefined,
      trip_type: tripType,
      pickup_date: format(date, "yyyy-MM-dd"),
      passengers: Number(form.passengers) || 1,
    };
    try {
      const res = await api.post("/bookings", payload);
      toast.success("Booking request received! We'll confirm shortly.");

      const wamsg =
        `Hello Bawajii Travels, I've placed a booking:\n\n` +
        `• Name: ${payload.name}\n` +
        `• Phone: ${payload.phone}\n` +
        `• Trip: ${TRIP_LABELS[payload.trip_type]}\n` +
        `• Pickup: ${payload.pickup_location}\n` +
        (payload.drop_location ? `• Drop: ${payload.drop_location}\n` : "") +
        `• Date: ${payload.pickup_date} ${payload.pickup_time}\n` +
        `• Vehicle: ${payload.vehicle_type} (${payload.passengers} pax)\n` +
        `• Booking ID: ${res.data.id}`;
      window.open(buildWhatsAppLink(wamsg), "_blank");

      setForm({
        name: "",
        phone: "",
        email: "",
        pickup_location: "",
        drop_location: "",
        pickup_time: "09:00",
        vehicle_type: "sedan",
        passengers: 2,
        notes: "",
      });
      setDate(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong";
      toast.error(typeof msg === "string" ? msg : "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="glass-panel rounded-3xl p-6 md:p-8" data-testid="booking-form">
      <Tabs value={tripType} onValueChange={setTripType}>
        <TabsList className="grid grid-cols-3 w-full bg-[color:var(--brand-forest)]/5 mb-6" data-testid="trip-type-tabs">
          <TabsTrigger value="local" data-testid="trip-tab-local">Local</TabsTrigger>
          <TabsTrigger value="outstation" data-testid="trip-tab-outstation">Outstation</TabsTrigger>
          <TabsTrigger value="airport" data-testid="trip-tab-airport">Airport</TabsTrigger>
        </TabsList>

        <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="pickup">
              <MapPin className="inline h-4 w-4 mr-1" /> Pickup Location
            </Label>
            <Input
              id="pickup"
              data-testid="pickup-input"
              value={form.pickup_location}
              onChange={(e) => upd("pickup_location", e.target.value)}
              placeholder="e.g. Home, Hotel Taj, Airport T3"
              className="mt-1 bg-white"
            />
          </div>
          {tripType !== "local" && (
            <div>
              <Label htmlFor="drop">
                <MapPin className="inline h-4 w-4 mr-1" /> Drop Location
              </Label>
              <Input
                id="drop"
                data-testid="drop-input"
                value={form.drop_location}
                onChange={(e) => upd("drop_location", e.target.value)}
                placeholder={tripType === "airport" ? "Airport Terminal" : "Destination city"}
                className="mt-1 bg-white"
              />
            </div>
          )}

          <div>
            <Label>
              <CalendarIcon className="inline h-4 w-4 mr-1" /> Pickup Date
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="date-picker-btn"
                  className="mt-1 w-full justify-start bg-white font-normal"
                >
                  {date ? format(date, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label htmlFor="time">
              <Clock className="inline h-4 w-4 mr-1" /> Pickup Time
            </Label>
            <Input
              id="time"
              type="time"
              data-testid="time-input"
              value={form.pickup_time}
              onChange={(e) => upd("pickup_time", e.target.value)}
              className="mt-1 bg-white"
            />
          </div>

          <div>
            <Label>Vehicle</Label>
            <Select value={form.vehicle_type} onValueChange={(v) => upd("vehicle_type", v)}>
              <SelectTrigger className="mt-1 bg-white" data-testid="vehicle-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="sedan">Sedan (4 seats)</SelectItem>
                <SelectItem value="suv">SUV (6-7 seats)</SelectItem>
                <SelectItem value="tempo">Tempo Traveller (10+ seats)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="pax">
              <Users className="inline h-4 w-4 mr-1" /> Passengers
            </Label>
            <Input
              id="pax"
              type="number"
              min={1}
              max={20}
              data-testid="passengers-input"
              value={form.passengers}
              onChange={(e) => upd("passengers", e.target.value)}
              className="mt-1 bg-white"
            />
          </div>

          <div>
            <Label htmlFor="name">Your Name</Label>
            <Input
              id="name"
              data-testid="name-input"
              value={form.name}
              onChange={(e) => upd("name", e.target.value)}
              placeholder="Full name"
              className="mt-1 bg-white"
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              data-testid="phone-input"
              value={form.phone}
              onChange={(e) => upd("phone", e.target.value)}
              placeholder="+91 98xxx xxxxx"
              className="mt-1 bg-white"
            />
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="email">Email (optional)</Label>
            <Input
              id="email"
              type="email"
              data-testid="email-input"
              value={form.email}
              onChange={(e) => upd("email", e.target.value)}
              placeholder="you@example.com"
              className="mt-1 bg-white"
            />
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              data-testid="notes-input"
              value={form.notes}
              onChange={(e) => upd("notes", e.target.value)}
              placeholder="Any special requests or landmarks"
              className="mt-1 bg-white"
            />
          </div>

          <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
            <p className="text-xs text-[color:var(--brand-muted)]">
              We&apos;ll send booking details to your WhatsApp &amp; email — no online payment required.
            </p>
            <Button
              type="submit"
              data-testid="booking-submit-btn"
              disabled={submitting}
              className="rounded-full bg-[color:var(--brand-forest)] hover:bg-[color:var(--brand-forest-dark)] text-white px-8 py-6 text-base"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Book Ride
            </Button>
          </div>
        </form>
      </Tabs>
    </div>
  );
};

export default BookingForm;
